import React, { useEffect, useState } from 'react';
import {
  Paper,
  IconButton,
  Box,
  Grid,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Divider,
  InputAdornment,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import Message from '../../tools/Message';
import Title from '../../../components/Title';
import EditIcon from '@mui/icons-material/Edit';
import SendTwoToneIcon from '@mui/icons-material/SendTwoTone';
import DownloadTwoToneIcon from '@mui/icons-material/DownloadTwoTone';
import HistoryIcon from '@mui/icons-material/History';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import { useNavigate } from 'react-router';
import DecompteMissionService from '../../../services/DecompteMissionService';
import FileService from '../../../services/FileService';
import { DataGrid } from '@mui/x-data-grid';

const DecompteTable = () => {
  const navigate = useNavigate();
  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] = useState(false);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({
    date_begin: '',
    date_end: '',
    reference: '',
    mission: '',
    status: '',
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    mission_planning_id: null,
    agenda_id: null,
    timesheet_id: null,
    transport_id: null,
    mission_decompte_id: null,
  });
  const [historyDialog, setHistoryDialog] = useState({ open: false, rows: [] });
  const [message, setMessage] = useState({
    open: false,
    color: 'green',
    messageInfo: '',
  });

  const { open, color, messageInfo } = message;

  function handleCloseMessage() {
    setMessage({ ...message, open: false });
  }

  const fetchData = async (currentFilters = {}) => {
    setOpenLoadingSimpleBackdrop(true);
    try {
      const response = await DecompteMissionService.findMissionsForDecompte(currentFilters);
      if (response && response.status) {
        setRows(response.data.map((item, index) => ({ id: index, ...item })));
      }
      setMessage({ open: true, color: 'green', messageInfo: 'Décomptes chargés avec succès.' });
    } catch (err) {
      setMessage({ open: true, color: 'red', messageInfo: 'Erreur lors du chargement des décomptes.' });
    } finally {
      setOpenLoadingSimpleBackdrop(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    fetchData(filters);
  };

  const handleResetFilters = () => {
    const cleared = {
      date_begin: '',
      date_end: '',
      reference: '',
      mission: '',
      status: '',
    };
    setFilters(cleared);
    fetchData(cleared);
  };
  const handleSendForValidation = async () => {
    const { mission_planning_id, agenda_id, timesheet_id, transport_id, mission_decompte_id } = confirmDialog;
    setOpenLoadingSimpleBackdrop(true);
    try{
      const resp = await DecompteMissionService.sendAndGenerate(mission_planning_id, agenda_id, timesheet_id, transport_id, mission_decompte_id);
      if(resp && resp.status){
        setMessage({ open: true, color: 'green', messageInfo: 'Le décompte a été envoyé avec succès.' });
        fetchData(filters);
      } else {
        setMessage({ open: true, color: 'red', messageInfo: resp?.message || "Erreur lors de l’envoi du décompte." });
      }
    }catch(error){
      setMessage({ open: true, color: 'red', messageInfo: error.message });
    }finally{
      setOpenLoadingSimpleBackdrop(false);
      setConfirmDialog({ open: false, mission_planning_id: null, agenda_id: null, timesheet_id: null, transport_id: null, mission_decompte_id: null });
    }
  };

  const handleDownloadFile = async (row) => {
    setOpenLoadingSimpleBackdrop(true);
    try{
      const fileId = row.file_decompte_pdf || row.file_decompte_excel;
      if(!fileId){
        setMessage({ open: true, color: 'warning', messageInfo: 'Aucun fichier de décompte généré pour cette mission.' });
        return;
      }
      const fileInfo = await FileService.getFilenameById(fileId);
      const filename = fileInfo?.data?.filename || fileInfo?.filename;
      if(!filename){
        setMessage({ open: true, color: 'red', messageInfo: 'Fichier de décompte introuvable.' });
        return;
      }
      const fileResponse = await FileService.readFile(filename);
      if(fileResponse?.data){
        const url = window.URL.createObjectURL(new Blob([fileResponse.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename.split('/').pop() || 'decompte.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    }catch(error){
      setMessage({ open: true, color: 'red', messageInfo: error.message || 'Erreur lors du téléchargement du décompte.' });
    }finally{
      setOpenLoadingSimpleBackdrop(false);
    }
  };

  const handleShowHistory = async (mission_decompte_id) => {
    if(!mission_decompte_id){
      return;
    }
    setOpenLoadingSimpleBackdrop(true);
    try{
      const resp = await DecompteMissionService.getHistory(mission_decompte_id);
      if(resp && resp.status){
        setHistoryDialog({ open: true, rows: resp.data || [] });
      } else {
        setMessage({ open: true, color: 'red', messageInfo: resp?.message || "Historique indisponible." });
      }
    }catch(error){
      setMessage({ open: true, color: 'red', messageInfo: error.message });
    }finally{
      setOpenLoadingSimpleBackdrop(false);
    }
  };

  const STATE_DECOMPTE = {
    PLANNING: { label: 'Élaboré', background: '#fff4e5', color: '#b54708' },
    PENDING: { label: 'En attente de validation', background: '#f4ebff', color: '#6941c6' },
    CONFIRMED: { label: 'Validé', background: '#ecfdf3', color: '#027a48' },
    REFUSED: { label: 'Refusé', background: '#fef3f2', color: '#b42318' },
    default: { label: 'Nouveau', background: '#eff8ff', color: '#175cd3' },
  };

  const formatDate = (value) => {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('fr-FR');
  };

  const columns = [
      { field: "reference", headerName: "Référence", minWidth: 150, flex: 0.8 },
      { field: "mission_title", headerName: "Objet de la mission", width: 280 },
      { field: "lieu", headerName: "Destination", width: 200 },
        {
        field: "date_begin",
        headerName: "Date de début",
        width: 140,
        renderCell: (params) => formatDate(params.row.date_debut),
      },
      {
        field: "date_end",
        headerName: "Date de fin",
        width: 140,
        renderCell: (params) => formatDate(params.row.date_fin),
      },
      {
        field: "mission_decompte_state",
        headerName: "Statut du décompte",
        width: 180,
        renderCell: (params) => {
          const state = params.row.mission_decompte_state;
          const { label, background, color } = STATE_DECOMPTE[state] || STATE_DECOMPTE.default;
          return <Chip label={label} size="small" sx={{ backgroundColor: background, color, fontWeight: 700 }} />;
        }
      },
      {
        field: "action",
        headerName: "Actions",
        width: 190,
        renderCell: (params) => (
          <>
            {["PLANNING", "REFUSED", null].includes(params.row.mission_decompte_state) && (
              <IconButton
                color="info"
                aria-label="Ouvrir le décompte"
                size="small"
                onClick={() => navigate(`/mission/decompte/add/${params.row.mission_planning_id}/${params.row.mission_planning_agenda_id}/${params.row.mission_timesheet_id || 0}/${params.row.transport_id || 0}?decompte_id=${params.row.mission_decompte_id || ''}`)}
              >
                <EditIcon />
              </IconButton>
            )}
            {["PLANNING", "REFUSED"].includes(params.row.mission_decompte_state) && (
              <IconButton
                color="success"
                aria-label="Envoyer le décompte pour validation"
                size="small"
                onClick={() => setConfirmDialog({
                  open: true,
                  mission_planning_id: params.row.mission_planning_id,
                  agenda_id: params.row.mission_planning_agenda_id,
                  timesheet_id: params.row.mission_timesheet_id,
                  transport_id: params.row.transport_id,
                  mission_decompte_id: params.row.mission_decompte_id,
                })}
              >
                <SendTwoToneIcon />
              </IconButton>
            )}
            {["CONFIRMED", "PENDING"].includes(params.row.mission_decompte_state) && (
              <IconButton
                color="warning"
                aria-label="Télécharger le décompte"
                size="small"
                onClick={() => handleDownloadFile(params.row)}
              >
                <DownloadTwoToneIcon />
              </IconButton>
            )}
            {params.row.mission_decompte_state && (
              <IconButton
                color="info"
                aria-label="Historique du décompte"
                size="small"
                onClick={() => handleShowHistory(params.row.mission_decompte_id)}
              >
                <HistoryIcon />
              </IconButton>
            )}
          </>
        )
      },
    ];

  return (
    <>
      <LoadingSimpleBackdrop openLoadingSimpleBackdrop={openLoadingSimpleBackdrop} />
      <Message open={open} color={color} messageInfo={messageInfo} handleClose={handleCloseMessage} />
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: { xs: 2, md: 2.5 },
          border: '1px solid #dbe5f0',
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f8fbff 0%, #ffffff 100%)',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <FilterAltRoundedIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={700}>Filtres de recherche</Typography>
        </Stack>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Référence"
              size="small"
              fullWidth
              value={filters.reference}
              onChange={(e) => handleFilterChange('reference', e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Objet ou destination"
              size="small"
              fullWidth
              value={filters.mission}
              onChange={(e) => handleFilterChange('mission', e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField select label="Statut du décompte" size="small" fullWidth value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
              <MenuItem value="">Tous</MenuItem>
              <MenuItem value="NEW">Nouveau</MenuItem>
              <MenuItem value="PLANNING">Élaboré</MenuItem>
              <MenuItem value="PENDING">En attente de validation</MenuItem>
              <MenuItem value="CONFIRMED">Validé</MenuItem>
              <MenuItem value="REFUSED">Refusé</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Date de début" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={filters.date_begin} onChange={(e) => handleFilterChange('date_begin', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Date de fin" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={filters.date_end} onChange={(e) => handleFilterChange('date_end', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
              <Button variant="contained" startIcon={<FilterAltRoundedIcon />} onClick={handleApplyFilters}>Filtrer</Button>
              <Button variant="outlined" startIcon={<RestartAltRoundedIcon />} onClick={handleResetFilters}>Réinitialiser</Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          border: '1px solid #dbe5f0',
          borderRadius: 3,
          boxShadow: '0 10px 30px rgba(30, 64, 175, 0.08)',
        }}
      >
        <Stack alignItems="center" sx={{ mb: 2 }}>
          <Title>Élaboration du décompte réel des missions</Title>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ height: 640, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            sx={{
              border: 0,
              '& .MuiDataGrid-columnHeaders': { backgroundColor: '#eff6ff', color: '#0f4c81', fontWeight: 700 },
              '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': { outline: 'none' },
              '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#f8fbff' },
              '& .MuiDataGrid-row:hover': { backgroundColor: '#eef6ff' },
              '& .MuiDataGrid-cell': { borderColor: '#e7edf5' },
            }}
          />
        </Box>
      </Paper>
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, mission_planning_id: null, agenda_id: null, timesheet_id: null, transport_id: null, mission_decompte_id: null })}
        aria-labelledby="decompte-send-title"
        aria-describedby="decompte-send-description"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="decompte-send-title">Confirmation d’envoi</DialogTitle>
        <DialogContent>
          <DialogContentText id="decompte-send-description">
            Souhaitez-vous envoyer ce décompte pour validation ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, mission_planning_id: null, agenda_id: null, timesheet_id: null, transport_id: null, mission_decompte_id: null })} variant='contained' color='error'>
            Fermer
          </Button>
          <Button onClick={handleSendForValidation} autoFocus variant='contained' color='info'>
            Envoyer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={historyDialog.open}
        onClose={() => setHistoryDialog({ open: false, rows: [] })}
        aria-labelledby="decompte-history-title"
        fullWidth
        maxWidth="md"
      >
        <DialogTitle id="decompte-history-title">Historique du décompte</DialogTitle>
        <DialogContent dividers>
          {historyDialog.rows.length === 0 ? (
            <DialogContentText>Aucun historique disponible.</DialogContentText>
          ) : (
            <div style={{ height: 360, width: '100%' }}>
              <DataGrid
                rows={historyDialog.rows.map((item, index) => ({ id: index, ...item }))}
                columns={[
                  { field: 'created_at', headerName: 'Date', width: 170 },
                  { field: 'action', headerName: 'Action', width: 120 },
                  { field: 'user_fullname', headerName: 'Utilisateur', width: 200 },
                  { field: 'old_values', headerName: 'Anciennes valeurs', width: 220 },
                  { field: 'new_values', headerName: 'Nouvelles valeurs', width: 220 },
                ]}
                pageSize={5}
                rowsPerPageOptions={[5, 10]}
                disableSelectionOnClick
              />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialog({ open: false, rows: [] })} variant='contained' color='info'>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DecompteTable;


